import { IListings, IConfigurations, IQueryParams } from './interface';
import { getListingData } from './getListingServices';
import ElementQueries from 'css-element-queries/src/ElementQueries';
import config from '../config';
import './widget.css';
import placeholderImage from '../assets/images/placeholder.png';

const html = require('./widget.html');
const cardHtml = require('./propertyCard.html');

let queryString: string;
let propertyCard: HTMLElement;

// Get container element by ID
const getContainer = (elementId: string): HTMLElement => {
	const container = document.getElementById(elementId);

	if (!container) {
		throw new Error(`Element with ID '${elementId}' not found`);
	}
	return container;
};

// Set content for text and image elements
const setElementContent = (element: Element | null, content: string | null, isImage: boolean = false) => {
	if (element) {
		if (isImage && element instanceof HTMLImageElement) {
			element.src = content || '';
			element.alt = `property-img-${propertyCard.id}`;
			element.onerror = () => {
				element.src = placeholderImage;
			};
		} else {
			element.textContent = content;
		}
	}
};

const hideElement = (element: Element | null) => {
	if (element) {
		(element as HTMLElement).style.display = 'none';
	}
};

const setBedroomsContent = (propertyCard: HTMLElement, property: IListings) => {
	if (!property.bedrooms) {
		hideElement(propertyCard.querySelector(`#bedroom_container_at`));
	} else {
		setElementContent(propertyCard.querySelector(`#count_bed_at`), property.bedrooms.toString());
		if (!property.bathrooms && !property.carparks) {
			hideElement(propertyCard.querySelector(`#bedroom_hori_line_at`));
		}
	}
};

const setBathroomsContent = (propertyCard: HTMLElement, property: IListings) => {
	if (!property.bathrooms) {
		hideElement(propertyCard.querySelector(`#bath_container_at`));
	} else {
		setElementContent(propertyCard.querySelector(`#count_bath_at`), property.bathrooms.toString());
	}
};

const setCarparksContent = (propertyCard: HTMLElement, property: IListings) => {
	if (!property.carparks) {
		hideElement(propertyCard.querySelector(`#parking_container_at`));
		hideElement(propertyCard.querySelector(`#bath_hori_line_at`));
	} else {
		setElementContent(propertyCard.querySelector(`#count_parking_at`), property.carparks.toString());
	}
};

// Create a property card from template and property data
const createPropertyCard = (property: IListings, index: number): HTMLElement => {
	const tempPropertyCardContainer = document.createElement('div');
	tempPropertyCardContainer.innerHTML = cardHtml.trim();
	propertyCard = tempPropertyCardContainer.firstElementChild?.cloneNode(true) as HTMLElement;

	// set dynamic Id for each card
	propertyCard.id = `card-${index}`;

	setElementContent(propertyCard.querySelector(`#${propertyCard.id} #property_img_at`), property.imageUrl, true);
	setElementContent(propertyCard.querySelector(`#${propertyCard.id} #property_add_at`), property.address);

	// Set content for bedrooms, bathrooms, and carparks
	setBedroomsContent(propertyCard, property);
	setBathroomsContent(propertyCard, property);
	setCarparksContent(propertyCard, property);

	return propertyCard;
};

// Append property cards to the card wrapper
const appendPropertyCards = (listings: IListings[], numberOfListings?: number) => {
	const cardWrapper = document.getElementById('property_card_wrapper_at');

	if (cardWrapper) {
		const fragment = document.createDocumentFragment();

		if (numberOfListings) {
			listings = listings.slice(0, numberOfListings);
		}

		listings.forEach((property, index) => {
			const propertyCard = createPropertyCard(property, index);
			fragment.appendChild(propertyCard);
			redirectToPropertyDetail(propertyCard, property.listingDetailLink);
		});

		cardWrapper.appendChild(fragment);
	}
};

// Redirect to view
const redirectToView = (logo: HTMLElement) => {
	logo.onclick = function () {
		window.open('https://view.com.au/', '_blank');
	};
};

const redirectToPropertyDetail = (showPropertyDetail: HTMLElement, listingDetailLink: string) => {
	const URL = `${queryString ? `${listingDetailLink}?${queryString}` : listingDetailLink}`;
	showPropertyDetail.onclick = function () {
		window.open(URL, '_blank');
	};
};

// hide widget
const hideWidget = () => {
	const widgetWrapper = document.getElementById('exclusive_homes_at');
	if (widgetWrapper) {
		widgetWrapper.style.display = 'none';
	}
};

// Query String
const addQueryParams = (config: IConfigurations): string => {
	const params: IQueryParams | undefined = config.urlQueryParams;
	if (params && Object.keys(params).length > 0) {
		return Object.entries(params)
			.filter(([key, value]) => value)
			.map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
			.join('&');
	}
	return '';
};

// Initialize widget
export const init = async (configurations: IConfigurations) => {
	try {
		const response = await getListingData(config.API_ENDPOINT, configurations?.location);

		if (response.data.listings && response.data.listings.length > 0) {
			if (!configurations.elementId) {
				throw new Error('Element ID is required for the widget to work');
			}

			queryString = addQueryParams(configurations);
			ElementQueries.init();
			const container = getContainer(configurations.elementId as string);
			container.innerHTML = html;
			const numberOfListings = configurations?.location?.size;
			appendPropertyCards(response.data.listings, numberOfListings);
			const logo = document.getElementById('logo_img_at');
			if (logo) {
				redirectToView(logo);
			}
		} else {
			hideWidget();
		}
	} catch (error: unknown) {
		hideWidget();
		console.error('Error fetching data from API:', error);
	}
};
